import React, { useContext, useState } from 'react';
import DOMPurify from 'dompurify';
import { Modal } from '@flightstats/airframe-react';
import { modalContentStyle } from "../../js/components/modal/Modal";
import { WaiverContext } from './WaiverEditConfigView';
import _ from 'lodash';

interface AirportParserProps {
  onClose: (airports?: string[]) => void;
  airportCodeObj: { currentAirportCodes: string[], isAirport: boolean }; // Add this property to match the usage
}

const AirportParser: React.FC<AirportParserProps> = ({ airportCodeObj, onClose }) => {
  const validAirportPattern = '[A-Z][A-Z0-9*]{2,3}';
  const validAirportRegex = new RegExp('^' + validAirportPattern + '$');
  const [text, setText] = useState('');
  const [highlightedText, setHighlightedText] = useState('');
  const [parsedAirportText, setParsedAirportText] = useState((airportCodeObj.currentAirportCodes || []).join(', '));
  const [showTextBox, setShowTextBox] = useState(true);

  const parseText = () => {
    let airports = extractAirports(text.toUpperCase());
    airports = airports.sort();
    setParsedAirportText(airports.join(', '));
    setShowTextBox(false);
  };

  const extractAirports = (text: string): string[] => {
    let airports = parseParenthesizedAirports(text);
    if (!_.isEmpty(airports)) {
      setHighlightedText(highlightAirports(text, airports, true));
      return airports;
    }

    airports = parseDelimitedAirports(text);
    setHighlightedText(highlightAirports(text, airports, false));
    return airports;
  };

  const parseParenthesizedAirports = (text: string): string[] => {
    const regex = new RegExp('\\((' + validAirportPattern + ')\\)', 'g');
    const airports: string[] = [];
    let match;
    while ((match = regex.exec(text)) !== null) {
      airports.push(match[1]);
    }
    return airports;
  };

  const parseDelimitedAirports = (text: string): string[] => {
    const airports = text.split(/[^A-Z0-9*]+/);
    return _.filter(airports, isValidAirport);
  };

  const isValidAirport = (airport: string): boolean => {
    return validAirportRegex.test(airport);
  };

  const highlightAirports = (text: string, airports: string[], includeParentheses: boolean): string => {
    if (_.isEmpty(airports)) {
      return text;
    }

    let remainingText = text;
    const highlightedChunks = airports.map((airport) => {
      const airportText = includeParentheses ? '(' + airport + ')' : airport;
      const endIndex = remainingText.indexOf(airportText) + airportText.length;

      let newChunk = remainingText.substring(0, endIndex);
      newChunk = highlightAirport(newChunk, airport, airportText);
      remainingText = remainingText.substring(endIndex);

      return newChunk;
    });

    highlightedChunks.push(remainingText);
    return highlightedChunks.join('').split('\n').join('<br>');
  };

  const highlightAirport = (text: string, airport: string, airportText: string): string => {
    const markedAirport = airportText.replace(airport, '<mark>' + airport + '</mark>');
    return text.replace(airportText, markedAirport);
  };

  const activateTextBox = () => {
    setShowTextBox(true);
  };

  const cancel = () => {
    onClose(airportCodeObj.currentAirportCodes);
  };

  const ok = () => {
    onClose(airports());
  };

  const airports = (): string[] => {
    if (_.isEmpty(parsedAirportText)) {
      return [];
    }
    const airports = parsedAirportText.split(/\s*,\s*/);
    return _.filter(airports, isValidAirport);
  };

  return (
    <Modal isOpen={true} contentStyle={modalContentStyle}>
      <div className="issue-resolution-modal modal-dialog modal-md">
        <form name="form" noValidate>
          <div className="title">
            Enter Affected Airports
            <a onClick={cancel}>
              <i id="modalCancelled" className="cancel pull-right glyphicon icon-white glyphicon-remove"></i>
            </a>
          </div>

          <div className="content">
            <div className="choices">
              <div className="row field-sep">
                <div className="col-md-3 fs-label fs-label-hxs hidden-sm hidden-xs">Enter Airports Text</div>
                <div className="col-md-3 fs-label fs-label-xs visible-sm visible-xs">Enter Airports Text</div>
                <div className="col-md-9 large-field-container">
                  {showTextBox ? (
                    <textarea
                      id="airportText"
                      className="text-area large-field"
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      autoFocus={showTextBox}
                    />
                  ) : (
                    <div className="text-area large-field" onClick={activateTextBox}>
                      <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(highlightedText) }}></div>
                    </div>
                  )}
                </div>
              </div>
              <div className="row field-sep">
                <div className="col-md-3"></div>
                <div className="col-md-9">
                  <input
                    type="button"
                    id="modalParseButton"
                    className="btn btn-default form-button"
                    onClick={parseText}
                    value="PARSE"
                  />
                </div>
              </div>
              <div className="row field-sep">
                <div className="col-md-3 fs-label fs-label-hxs hidden-sm hidden-xs">Affected Airports (comma-separated)</div>
                <div className="col-md-3 fs-label fs-label-xs visible-sm visible-xs">Affected Airports (comma-separated)</div>
                <div className="col-md-9">
                  <textarea
                    className="text-area large-field"
                    value={parsedAirportText}
                    onChange={(e) => setParsedAirportText(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="action">
              <input type="button" id="modalSubmitted" className="btn btn-default" value="APPLY AFFECTED AIRPORTS" onClick={ok} />
              <input type="button" id="modalCancelSubmitted" className="btn btn-default margin-left-5" onClick={cancel} value="CANCEL" />
            </div>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default AirportParser;